import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Clock,
  RefreshCw,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { fetchLeaveReportSummary } from "../../../Moduels/leave/leaveApi";
import { HrBtn, HrEmptyState } from "../../hr/HrDesignSystem";
import BrixigoPageLoader from "../../common/BrixigoPageLoader";
import { useLeaveOrg } from "./useLeaveOrg";
import { LeaveKpi } from "./leaveTheme";

const COLORS = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#f97316", "#ec4899"];

/* Attendance-style KPI; tone picked from the legacy colour prop. */
const KPI_TONE_BY_COLOR = { "#f59e0b": "amber", "#10b981": "green", "#ef4444": "red", "#2563eb": "indigo" };

function KpiCard({ label, value, color, suffix }) {
  return <LeaveKpi label={label} value={value} suffix={suffix} tone={KPI_TONE_BY_COLOR[String(color).toLowerCase()] || "indigo"} />;
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100">
        <h3 className="text-[13px] font-bold text-slate-800">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white text-[12px] rounded-lg px-3 py-2 shadow-xl">
      <p className="font-bold mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: entry.color }} />
          {entry.name}: <b>{entry.value}</b>
        </p>
      ))}
    </div>
  );
}

function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-slate-900 text-white text-[12px] rounded-lg px-3 py-2 shadow-xl">
      <p className="font-bold">{d.name}</p>
      <p>{d.value} request{d.value !== 1 ? "s" : ""} ({((d.payload.percent || 0) * 100).toFixed(0)}%)</p>
    </div>
  );
}

function getYearRange(year) {
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

export default function LvLeaveAnalytics() {
  const { orgId, loading: orgLoading } = useLeaveOrg();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const { from, to } = getYearRange(year);
      const data = await fetchLeaveReportSummary({ organisation_id: orgId, from_date: from, to_date: to });
      setReport(data);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [orgId, year]);

  useEffect(() => { load(); }, [load]);

  const byTypeData = useMemo(() => {
    if (!report?.by_type?.length) return [];
    const total = report.by_type.reduce((s, r) => s + (r.count || 0), 0);
    return report.by_type.map((r) => ({
      name: r.name || r.code,
      value: r.count || 0,
      days: r.days || 0,
      percent: total > 0 ? (r.count || 0) / total : 0,
    }));
  }, [report]);

  const barData = useMemo(() => {
    if (!report?.by_type?.length) return [];
    return report.by_type.map((r) => ({
      name: r.name || r.code,
      Requests: r.count || 0,
      Days: r.days || 0,
    }));
  }, [report]);

  const totalRequests = report?.total_requests || 0;
  const approvedPct = totalRequests > 0 ? Math.round(((report?.approved || 0) / totalRequests) * 100) : 0;

  if (orgLoading) return <BrixigoPageLoader compact message="Loading..." />;

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[12px] font-semibold text-slate-600 mb-1 block">Year</label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-28 px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-[13px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition"
            />
          </div>
          <HrBtn onClick={load} variant="primary" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Loading..." : "Refresh"}
          </HrBtn>
        </div>
      </div>

      {loading ? (
        <BrixigoPageLoader compact message="Loading analytics..." />
      ) : !report ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
          <HrEmptyState
            icon={BarChart3}
            title="No analytics data"
            description="Initialize the leave module and run at least one accrual cycle to see analytics here."
          />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="att-mvp-kpi-grid">
            <KpiCard icon={BarChart3} label="Total Requests" value={totalRequests} color="#2563eb" accent="#7c3aed" />
            <KpiCard icon={TrendingUp} label="Approval Rate" value={approvedPct} suffix="%" color="#10b981" accent="#06b6d4" />
            <KpiCard icon={Clock} label="Pending" value={report.pending || 0} color="#f59e0b" accent="#fb923c" />
            <KpiCard icon={Users} label="LOP Days" value={report.lop_days || 0} color="#ef4444" accent="#f472b6" />
          </div>

          {/* Status breakdown bar */}
          {totalRequests > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-3">Request Status Breakdown</p>
              <div className="flex rounded-full overflow-hidden h-3">
                {[
                  { key: "approved", value: report.approved || 0, color: "#10b981" },
                  { key: "pending", value: report.pending || 0, color: "#f59e0b" },
                  { key: "rejected", value: report.rejected || 0, color: "#ef4444" },
                ].filter((s) => s.value > 0).map((s) => (
                  <div
                    key={s.key}
                    className="h-full transition-all duration-700"
                    style={{
                      width: `${(s.value / totalRequests) * 100}%`,
                      backgroundColor: s.color,
                    }}
                    title={`${s.key}: ${s.value}`}
                  />
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mt-2">
                {[
                  { label: "Approved", value: report.approved || 0, color: "#10b981" },
                  { label: "Pending", value: report.pending || 0, color: "#f59e0b" },
                  { label: "Rejected", value: report.rejected || 0, color: "#ef4444" },
                ].map((s) => (
                  <div key={s.label} className="flex items-center gap-1.5 text-[12px]">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                    <span className="text-slate-600 font-medium">{s.label}</span>
                    <span className="font-bold text-slate-800">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Charts row */}
          {byTypeData.length > 0 && (
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Bar chart */}
              <ChartCard title="Requests & Days by Leave Type">
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={barData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        axisLine={{ stroke: "#e2e8f0" }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="Requests" fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={36} />
                      <Bar dataKey="Days" fill="#06b6d4" radius={[4, 4, 0, 0]} maxBarSize={36} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>

              {/* Donut chart */}
              <ChartCard title="Leave Type Distribution">
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={byTypeData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={3}
                        dataKey="value"
                        nameKey="name"
                        stroke="none"
                      >
                        {byTypeData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<PieTooltip />} />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value) => <span className="text-[12px] text-slate-600 font-medium">{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
            </div>
          )}

          {/* Detailed table */}
          {byTypeData.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100">
                <h3 className="text-[13px] font-bold text-slate-800">Leave Type Details</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-5 py-2.5 text-[11px] uppercase tracking-wider font-bold text-slate-500">Type</th>
                      <th className="text-right px-5 py-2.5 text-[11px] uppercase tracking-wider font-bold text-slate-500">Requests</th>
                      <th className="text-right px-5 py-2.5 text-[11px] uppercase tracking-wider font-bold text-slate-500">Days</th>
                      <th className="text-right px-5 py-2.5 text-[11px] uppercase tracking-wider font-bold text-slate-500">Share</th>
                      <th className="px-5 py-2.5 text-[11px] uppercase tracking-wider font-bold text-slate-500">Distribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byTypeData.map((row, i) => (
                      <tr key={row.name} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <span
                              className="w-2.5 h-2.5 rounded-full shrink-0"
                              style={{ background: COLORS[i % COLORS.length] }}
                            />
                            <span className="font-semibold text-slate-800">{row.name}</span>
                          </div>
                        </td>
                        <td className="text-right px-5 py-3 font-bold text-slate-700 tabular-nums">{row.value}</td>
                        <td className="text-right px-5 py-3 font-bold text-slate-700 tabular-nums">{row.days}</td>
                        <td className="text-right px-5 py-3 font-bold text-blue-600 tabular-nums">{(row.percent * 100).toFixed(0)}%</td>
                        <td className="px-5 py-3">
                          <div className="w-full bg-slate-100 rounded-full h-2 min-w-[60px]">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{
                                width: `${row.percent * 100}%`,
                                backgroundColor: COLORS[i % COLORS.length],
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Insights */}
          {totalRequests > 0 && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-5">
              <h3 className="text-[13px] font-bold text-blue-900 mb-2 flex items-center gap-1.5">
                <TrendingUp size={14} /> Insights
              </h3>
              <ul className="space-y-1.5 text-[13px] text-blue-800">
                <li>
                  <b>{approvedPct}%</b> of all leave requests were approved in {year}.
                </li>
                {(report.rejected || 0) > 0 && (
                  <li>
                    <b>{report.rejected}</b> request{report.rejected !== 1 ? "s were" : " was"} rejected — review leave policies if this seems high.
                  </li>
                )}
                {(report.lop_days || 0) > 0 && (
                  <li>
                    <b>{report.lop_days}</b> LOP day{report.lop_days !== 1 ? "s" : ""} recorded — employees may need additional leave allocation.
                  </li>
                )}
                {byTypeData.length > 0 && (
                  <li>
                    <b>{byTypeData[0].name}</b> is the most used leave type with{" "}
                    <b>{byTypeData[0].value}</b> request{byTypeData[0].value !== 1 ? "s" : ""}.
                  </li>
                )}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
